import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import BookCell from "./BookCell";

interface Book {
  book_id: number;
  book_title: string;
  author_id: number;
  book_description?: string;
  book_price?: number;
  book_cover_path?: string;
  book_genres?: string[];
  book_quantity?: number;
  book_page_count?: number;
  book_publication_year?: number;
  publisher_id?: number;
  is_new: boolean;
}

const PageContainer = styled.div`
  padding: 20px;
  font-family: "IBM Plex Mono", monospace;
  background: #f7f7f7;
  min-height: 100vh;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;
`;

const Title = styled.h1`
  font-size: 36px;
  color: #a13b16;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 12px 20px;
  background: #a13b16;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  cursor: pointer;
  font-family: "IBM Plex Mono", monospace;
  transition: background 0.2s ease;

  &:hover {
    background: #8b2f0f;
  }
`;

const SearchAndFilters = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  font-family: "IBM Plex Mono", monospace;
  flex: 1;
  min-width: 250px;

  &:focus {
    outline: none;
    border-color: #a13b16;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 10px;
  font-size: 14px;
  font-family: "IBM Plex Mono", monospace;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #a13b16;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "IBM Plex Mono", monospace;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #a13b16;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
`;

const BooksGrid = styled.div`((a, b) =>
          getAuthorName(a.author_id).localeCompare(getAuthorName(b.author_id))
        );
        break;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  justify-content: space-around;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: "IBM Plex Mono", monospace;
  color: #666;
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  color: #a13b16;
  margin-bottom: 10px;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 18px;
  color: #a13b16;
`;

const BookListView: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [filterBy, setFilterBy] = useState("all");
  const [authors, setAuthors] = useState<{ author_id: number; author_name: string }[]>([]);
  const [publishers, setPublishers] = useState<{ publisher_id: number; name: string }[]>([]);
  const navigate = useNavigate();

  const getAuthorName = (author_id?: number) =>
    authors.find((a) => a.author_id === author_id)?.author_name || "—";

  const getPublisherName = (publisher_id?: number) =>
    publishers.find((p) => p.publisher_id === publisher_id)?.name || "";

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/books`);
        const data = await response.json();
        setBooks(data);
        setFilteredBooks(data);
      } catch (err) {
        console.error("Failed to fetch books:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  useEffect(() => {
    const fetchRefs = async () => {
      try {
        const [authorsRes, publishersRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}/authors`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers`)
        ]);
        const authorsData = await authorsRes.json();
        const publishersData = await publishersRes.json();

        setAuthors(authorsData);
        setPublishers(publishersData);
      } catch (err) {
        console.error("Помилка завантаження авторів/видавництв", err);
      }
    };

    fetchRefs();
  }, []);

  useEffect(() => {
    let filtered = books.filter(book => {
      const matchesSearch = 
        book.book_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getAuthorName(book.author_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.book_description && book.book_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        getPublisherName(book.publisher_id).toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterBy === "all" || 
        (filterBy === "available" && book.book_quantity && book.book_quantity > 0) ||
        (filterBy === "low_stock" && book.book_quantity && book.book_quantity <= 5 && book.book_quantity > 0) ||
        (filterBy === "out_of_stock" && (!book.book_quantity || book.book_quantity === 0)) ||
        (filterBy === "is_new" && (book.is_new));

      return matchesSearch && matchesFilter;
    });

    switch (sortBy) {
      case "newest":
        filtered.sort((a, b) => (b.book_publication_year || 0) - (a.book_publication_year || 0));
        break;
      case "oldest":
        filtered.sort((a, b) => (a.book_publication_year || 0) - (b.book_publication_year || 0));
        break;
      case "title_asc":
        filtered.sort((a, b) => a.book_title.localeCompare(b.book_title));
        break;
      case "title_desc":
        filtered.sort((a, b) => b.book_title.localeCompare(a.book_title));
        break;
      case "price_low":
        filtered.sort((a, b) => (a.book_price || 0) - (b.book_price || 0));
        break;
      case "price_high":
        filtered.sort((a, b) => (b.book_price || 0) - (a.book_price || 0));
        break;
      case "author":
        filtered.sort((a, b) =>
          getAuthorName(a.author_id).localeCompare(getAuthorName(b.author_id))
        );
        break;
      default:
        break;
    }

    setFilteredBooks(filtered);
  }, [books, searchTerm, sortBy, filterBy]);

  const stats = {
    total: books.length,
    available: books.filter(book => book.book_quantity && book.book_quantity > 0).length,
    lowStock: books.filter(book => book.book_quantity && book.book_quantity <= 5 && book.book_quantity > 0).length,
    outOfStock: books.filter(book => !book.book_quantity || book.book_quantity === 0).length,
    totalValue: books.reduce((sum, book) => sum + ((book.book_price || 0) * (book.book_quantity || 0)), 0)
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Завантаження книг...</LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TopBar>
        <Title>Книги ({filteredBooks.length})</Title>
        <ButtonGroup>
          <Button onClick={() => navigate("/admin")}>Головна</Button>
          <Button onClick={() => navigate("/admin/books/create")}>Додати книгу</Button>
        </ButtonGroup>
      </TopBar>

      <StatsBar>
        <StatItem>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Всього книг</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.available}</StatValue>
          <StatLabel>В наявності</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.lowStock}</StatValue>
          <StatLabel>Мало залишилось</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{stats.outOfStock}</StatValue>
          <StatLabel>Закінчились</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>₴{stats.totalValue.toFixed(0)}</StatValue>
          <StatLabel>Загальна вартість</StatLabel>
        </StatItem>
      </StatsBar>

      <SearchAndFilters>
        <SearchInput
          type="text"
          placeholder="🔍 Пошук за назвою, автором, описом..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={filterBy}
          onChange={(e) => setFilterBy(e.target.value)}
        >
          <option value="all">Всі книги</option>
          <option value="available">В наявності</option>
          <option value="low_stock">Мало залишилось</option>
          <option value="out_of_stock">Закінчились</option>
          <option value="is_new">Тільки новинки</option>
        </FilterSelect>
        <FilterSelect
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="newest">Найновіші</option>
          <option value="oldest">Найстарші</option>
          <option value="title_asc">Назва А-Я</option>
          <option value="title_desc">Назва Я-А</option>
          <option value="author">За автором</option>
          <option value="price_low">Дешевші спочатку</option>
          <option value="price_high">Дорожчі спочатку</option>
        </FilterSelect>
      </SearchAndFilters>

      {filteredBooks.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>
            {searchTerm || filterBy !== "all" ? "Книги не знайдено" : "Книг поки немає"}
          </EmptyStateTitle>
          <EmptyStateText>
            {searchTerm || filterBy !== "all" 
              ? "Спробуйте змінити параметри пошуку або фільтри"
              : "Додайте першу книгу"
            }
          </EmptyStateText>
          {(!searchTerm && filterBy === "all") && (
            <Button onClick={() => navigate("/admin/books/create")}>
              Додати першу книгу
            </Button>
          )}
        </EmptyState>
      ) : (
        <BooksGrid>
        {filteredBooks.map((book) => {
          const author = authors.find((a) => a.author_id === book.author_id);
          const publisher = publishers.find((p) => p.publisher_id === book.publisher_id);

          return (
            <BookCell
              key={book.book_id}
              book={{
                ...book,
                author_name: author?.author_name || "Невідомий автор",
                publisher_name: publisher?.name || "Невідоме видавництво"
              }}
              onClick={() => navigate(`/admin/books/${book.book_id}`)}
            />
          );
        })}
      </BooksGrid>
      )}
    </PageContainer>
  );
};

export default BookListView;